// apps/admin/src/pages/auth/EnrollMFAPage.tsx
//
// Route: /mfa/setup — native GCIP TOTP enrolment for the founder admin portal.
// Re-pointed off the bespoke setupMFA / verifyAndEnableMFA callables onto the
// modular Web SDK (multiFactor().getSession() → TotpMultiFactorGenerator →
// enroll()). AdminRouteGuard sends a founder without a factor here; on success
// we hard-reload to '/' so AdminContext recomputes `mfaEnrolled` from the
// freshly-enrolled user and the guard lets the founder through.

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminContext';
import { auth } from '../../firebaseConfig';
import MfaEnrollmentCard from '../../components/auth/MfaEnrollmentCard';

const AdminAuthBackground: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center p-4 bg-gray-900 bg-gradient-to-br from-indigo-900/50 via-gray-900 to-fuchsia-900/50 text-white">
    <div className="absolute inset-0 z-0 overflow-hidden">
      <div className="absolute top-0 -left-1/4 w-96 h-96 bg-indigo-600/30 rounded-full filter blur-3xl opacity-50 animate-pulse"></div>
      <div className="absolute bottom-0 -right-1/4 w-96 h-96 bg-fuchsia-400/30 rounded-full filter blur-3xl opacity-50 animation-delay-4000 animate-pulse"></div>
    </div>
    {children}
  </div>
);

const EnrollMFAPage: React.FC = () => {
  const { user, mfaEnrolled, loading, signOut } = useAdminAuth();
  // AdminContext exposes `user` as a SPREAD copy of the Firebase User
  // (`{ ...firebaseUser, role }`), which loses the SDK prototype. The native
  // MFA APIs (`multiFactor()`, `getSession()`, `enroll()`) refuse anything that
  // is not the SDK's own User object, and `hasEnrolledTotp(spreadCopy)` throws
  // during render (prod, 2026-08-18: the founder saw the app error boundary on
  // /mfa/setup). So: enrolment state comes from the context, which computed it
  // from the real object, and the card gets the live SDK user.
  const sdkUser = auth.currentUser;

  if (loading) {
    return (
      <AdminAuthBackground>
        <p className="text-lg z-10">Loading…</p>
      </AdminAuthBackground>
    );
  }

  if (!user || !sdkUser) {
    return <Navigate to="/login" replace />;
  }

  // GCIP allows only one TOTP factor per account; an already-enrolled founder
  // has nothing to enrol here. Send them into the portal rather than into a
  // maximum-second-factor-count-exceeded dead-end.
  if (mfaEnrolled) {
    return <Navigate to="/" replace />;
  }

  const handleEnrolled = (): void => {
    // Full reload so AdminContext re-reads enrolledFactors and the guard admits.
    window.location.assign('/');
  };

  const handleReauth = (): void => {
    void signOut().finally(() => window.location.assign('/login'));
  };

  return (
    <AdminAuthBackground>
      <MfaEnrollmentCard user={sdkUser} onEnrolled={handleEnrolled} onReauth={handleReauth} />
    </AdminAuthBackground>
  );
};

export default EnrollMFAPage;
